import { loopExecuteFn, objectHasOwnProperty } from '../js/utils';
import Log from '../js/Log';
import { COMMUNITION_ERRORS } from './ErrorHandler';

export const log = new Log({
  log_type: '2',
  frank_status: '30'
});

export default class EventEmmiter {
  constructor() {
    this.timer = null;
    this.events = {};
  }

  on(type, callback) {
    if (objectHasOwnProperty(this.events, type)) {
      this.events[type].push(callback);
    } else {
      this.events[type] = [callback];
    }
  }

  off(type) {
    if (objectHasOwnProperty(this.events, type)) {
      delete this.events[type];
    }
  }

  emmit(type) {
    // eslint-disable-next-line prefer-rest-params
    const args = Array.prototype.slice.call(arguments, 1);
    const cxt = this;

    if (objectHasOwnProperty(this.events, type)) {
      this.events[type].forEach((fn) => {
        fn.apply(cxt, args);
      });
    }
  }

  /**
   * @description 执行操作封装，添加对应事件监听，并对命令进行循环执行
   * @param {objecj} { max, interval, step, executeFn }
   * @property {null|undefined|number} max 最大循环执行次数
   * @property {number} interval 循环间隔时间，单位：ms
   * @property {string} step 步骤描述
   * @property {function} executeFn 执行的操作
   * @returns {Promise}
   * @memberof RFID
   */
  execute({
    session,
    max,
    interval,
    step,
    executeFn,
    noSession = false,
    resolveCode = true
  }) {
    console.log(`execute: ${session || ''}  ${step}`);
    this.currentStep = step;
    const that = this;

    // 重试次数,与loopExecuteFn方法里的重试机制不一样，retryCount适用于原生接口调用成功并返回错误的情况下的重试
    let retryCount = 0;

    return new Promise((resolve, reject) => {
      function loopExecute() {
        that.on(`${step}_success`, (sessionId, ...args) => {
          console.log(step, sessionId, args);
          if (noSession) {
            resolve([sessionId, ...args]);
          }
          const { count } = that.timer;
          that.endStep(step);

          if (session && parseInt(sessionId, 10) !== session) {
            console.error('sessionId不一致,丢弃');
            const error = new Error('sessionId不一致');
            error.code = '-3';
            error.count = count();
            reject(error);
          }

          resolveCode
            // eslint-disable-next-line no-use-before-define
            ? resolveCallback(...args)
            : resolve({
              count: count(),
              data: args.length > 1 ? args : args[0]
            });
        });
        that.on(`${step}_error`, (error) => {
          let count = error.count;
          if (!count) {
            count = that.timer.count;
          }
          // eslint-disable-next-line no-param-reassign
          error.count = count();
          that.endStep(step);
          // 上报错误
          reject(error);
        });

        that.timer = loopExecuteFn({
          max,
          interval,
          executeFn,
          errorFn: (count, err) => {
            const error = new Error(err || '执行超时');
            error.code = '-1';
            error.count = () => count;
            that.emmit(`${step}_error`, error);
          }
        });
      }

      // eslint-disable-next-line consistent-return
      function resolveCallback(code, ...args) {
        const errCode = parseInt(code, 10);
        if (errCode === 0) {
          return resolve({
            count: that.timer.count(),
            data: args.length > 1 ? args : args[0]
          });
        }
        let codeError = new Error();
        if (errCode in COMMUNITION_ERRORS) {
          codeError = new Error(COMMUNITION_ERRORS[errCode].description);
          codeError.retry = COMMUNITION_ERRORS[errCode].retry;
          retryCount++;

          if (codeError.retry && (retryCount <= max || !max)) {
            return new Promise((solve) => {
              setTimeout(() => {
                solve();
              }, interval);
            }).then(() => { loopExecute(); });
          }

          const expireError = new Error('执行超时');
          expireError.code = '-1';
          expireError.count = retryCount;
          return reject(expireError);
        }

        codeError = new Error('未知错误');
        codeError.code = code;
        codeError.count = that.timer.count();
        codeError.originData = [code, ...args];
        return reject(codeError);
      }

      loopExecute();
    });
  }

  /**
   * @description 结束指定步骤或结束当前步骤
   * @param {string} step 步骤描述
   * @memberof EventEmmiter
   */
  endStep(step) {
    this.off(`${step || this.currentStep}_success`);
    this.off(`${step || this.currentStep}_error`);
    // this.currentStep = '';
    if (this.timer) {
      this.timer.stop();
    }
  }

  emmitCallback(...args) {
    this.emmit.apply(this, [`${this.currentStep}_success`, ...args]);
  }
}

export const event = new EventEmmiter();
